import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  OnInit,
  OnDestroy,
  ViewChild,
  NgZone,
  ChangeDetectionStrategy,
  signal,
  computed,
} from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { Title } from '@angular/platform-browser';
import {
  ActivatedRoute,
  Router,
  RouterEvent,
  Event,
  NavigationEnd,
  RouterOutlet,
} from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { DropdownObject, SearchBox_onChangeOverrideEvent } from '@inmoment/the-kitchen';

import { AppSharedModule } from 'app/app-shared.module';
import { SimpleDialogButtonColor } from 'app/components/simple-dialog.component';
import { FieldConstants } from 'app/constants/constants';
import { HasTitle } from 'app/has-title';
import { AuthService, Session } from 'app/providers/bridge-api/auth.service';
import { SemantriaPreferences } from 'app/providers/bridge-api/preferences.service';
import { RealtimeMessage } from 'app/providers/realtime.service';
import { GroupService } from 'app/providers/semantria-api/group.service';
import { Field, FieldService, SortTypes } from 'app/providers/storage-api/field.service';
import { Project, ProjectService } from 'app/providers/storage-api/project.service';
import { StorageListResult } from 'app/providers/storage-api.service';
import { AlertService } from 'app/services/alert.service';
import { DialogService } from 'app/services/dialog.service';
import { IconService } from 'app/services/icon.service';
import { LoggerService } from 'app/services/logger.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import {
  FieldCreateModalComponent,
  FieldResult,
} from '../field-create-modal/field-create-modal.component';
import { FieldListFilterComponent } from '../field-list-filter/field-list-filter.component';

@EnableClassTelemetry()
@Component({
  selector: 'app-fields',
  templateUrl: './field-list.component.html',
  styleUrls: ['./field-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AppSharedModule, RouterOutlet, FieldListFilterComponent, FieldCreateModalComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class FieldListComponent extends HasTitle implements OnInit, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  @ViewChild(MatMenuTrigger, { static: true }) sortMenuTrigger: MatMenuTrigger;

  isLoading = signal(true);

  currentSort = signal(SortTypes.NAME);

  reverseSort = signal(false);

  //Actual sort value(currentSort and if reverseSort)
  readonly fieldsSort = computed(() => {
    const sort = this.currentSort() * (this.reverseSort() ? -1 : 1);
    return sort.toString();
  });

  readonly sortedTooltip = computed(() => {
    const t = this.translocoService.translateObject('field-list');
    const match = this.fieldsListSortData().find((arr) => arr.id === this.fieldsSort());
    return `${t['metadata-list-sorted-by']} ${match?.title ?? ''}`;
  });

  allFields: StorageListResult<Field> = {};
  fieldList = signal<Field[]>([]);
  //Fields to display after filtering and search
  displayedFields = signal<Field[]>([]);
  filteredFields = signal<Field[]>([]);
  searchText = '';

  selectedField = signal<Field>(undefined);

  fieldsListSortData = signal<DropdownObject[]>([
    { title: '', id: '1' },
    { title: '', id: '-1' },
    { title: '', id: '2' },
    { title: '', id: '-2' },
  ]);

  fieldsRightPlaceholder = signal({
    title: '',
    subtitle: '',
  });

  unusedFields = signal<Field[]>([]);
  deleteUnusedFieldsTooltip = signal('');
  isUnusedFieldsLoading = signal(false);
  isUnusedFieldDeleting = false;
  showDeleteUnusedFieldsButton = signal(false);

  typeTranslations = signal({
    keyword: '',
    long: '',
    date: '',
    double: '',
    calculated: '',
  });

  private DEFAULT_FIELD_ICON = 'assets/icons/field-icon.svg';
  readonly GLOBAL = FieldConstants.GLOBAL;

  private session: Session;
  private canChangeFields = signal(false);
  groupId: string;

  private unsub$ = new Subject<void>();
  showNewMetadataFieldModal = signal(false);
  readonly canShowActionButtons = computed(
    () => this.fieldList().length > 0 && this.canChangeFields()
  );
  isFilterFieldsPopoutVisible = signal(false);
  areFiltersApplied = signal(false);
  projects = signal<Project[]>([]);
  fieldListFilterIds = [];

  constructor(
    protected titleService: Title,
    private router: Router,
    private route: ActivatedRoute,
    private alertService: AlertService,
    private fieldService: FieldService,
    private iconService: IconService,
    private preferencesService: SemantriaPreferences,
    private authService: AuthService,
    private groupService: GroupService,
    private dialogService: DialogService,
    private translocoService: TranslocoService,
    private ngZone: NgZone,
    private projectService: ProjectService,
    private logger: LoggerService
  ) {
    super(titleService);

    const params = this.route.snapshot.paramMap;
    this.groupId = params.get('groupId');
  }

  async ngOnInit() {
    this.translocoService
      .selectTranslateObject('field-list')
      .pipe(takeUntil(this.unsub$))
      .subscribe((t) => {
        this.setTitle(t['fields-title']);
        this.fieldsListSortData.set([
          { title: t['date-ascending'], id: '1' },
          { title: t['date-descending'], id: '-1' },
          { title: t['name-ascending'], id: '2' },
          { title: t['name-descending'], id: '-2' },
        ]);

        if (!this.deleteUnusedFieldsTooltip()) {
          this.deleteUnusedFieldsTooltip.set(t['delete-unused-fields']);
        }

        this.fieldsRightPlaceholder.set({
          title: t['no-fields-title'],
          subtitle: t['no-fields-subtitle'],
        });

        this.typeTranslations.update((current) => {
          const updated = { ...current };
          for (const key of Object.keys(updated)) {
            updated[key] = t['field-types'][key];
          }
          return updated;
        });

        this.internalProps = {
          searchBox: {
            placeholder: t['search-by-field'],
            onChangeOverride: ($event: SearchBox_onChangeOverrideEvent) => {
              this.ngZone.run(() => {
                this.searchText = $event.searchText;
                this.filterFieldsBySearch();
              });
            },
          },
        };
      });

    this.router.events
      .pipe(
        takeUntil(this.unsub$),
        filter((e: Event | RouterEvent): e is RouterEvent => e instanceof NavigationEnd)
      )
      .subscribe((e: RouterEvent) => {
        if (e.url === `/groups/${this.groupId}/fields`) {
          this.onFieldSelected(this.displayedFields()[0]);
        }
      });

    this.fieldService
      .onChange()
      .pipe(takeUntil(this.unsub$))
      .subscribe((fields) => {
        if (fields) {
          let newSelectedId = null;

          if (
            this.fieldList().length &&
            !fields.items.some((f) => f.id === this.selectedField().id) &&
            fields.count !== 0
          ) {
            const deletedIdx = this.fieldList().findIndex((p) => p.id === this.selectedField().id);

            if (deletedIdx === this.fieldList().length - 1) {
              newSelectedId = this.fieldList()[deletedIdx - 1].id;
            } else {
              newSelectedId = this.fieldList()[deletedIdx + 1].id;
            }
          }

          this.allFields = fields;

          this.setCurrentFields(newSelectedId);
        }
      });

    this.fieldService.realtimeEvent$
      .pipe(takeUntil(this.unsub$))
      .subscribe((data: RealtimeMessage<Field>) => {
        const fieldIdx = this.fieldList().findIndex((f) => f.fieldName === data.object.fieldName);
        if (data.verb === 'CREATE') {
          if (fieldIdx === -1) {
            this.fieldList.update((fields) => [...fields, data.object]);
            this.filterFieldsBySearch();
            this.fieldService.sortFields(
              this.displayedFields(),
              this.currentSort(),
              this.reverseSort()
            );
          }
        } else if (data.verb === 'UPDATE') {
          if (fieldIdx !== -1) {
            this.fieldList.update((fields) =>
              fields.map((f, i) => (i === fieldIdx ? data.object : f))
            );
            this.filterFieldsBySearch();
            this.fieldService.sortFields(
              this.displayedFields(),
              this.currentSort(),
              this.reverseSort()
            );
          }
        } else if (data.verb === 'DELETE') {
          if (fieldIdx !== -1) {
            this.fieldList.update((fields) => fields.filter((_, i) => i !== fieldIdx));
            this.filterFieldsBySearch();
          }
        }
      });

    for (const id of FieldConstants.FILTERTYPES) {
      this.fieldListFilterIds.push(`${id}-dropdown-menu`);
    }

    try {
      const [_, _session, preferences] = await Promise.all([
        this.groupService.refreshAndSelectActiveGroup(this.groupId),
        this.authService.getSession(),
        this.preferencesService.getPreferences(),
      ]);
      const storedSortId = +preferences.fieldSorting || '';

      if (storedSortId) {
        this.populateStoredSortValue(storedSortId);
      } else {
        this.populateStoredSortValue(this.currentSort());
      }
      await this.refresh();

      this.canChangeFields.set(!this.authService.isReadOnly);

      const params = this.route.firstChild?.snapshot.paramMap;
      if (!params || !params.get('fieldId')) {
        this.onFieldSelected(this.displayedFields()[0]);
      }

      this.route.firstChild?.params.subscribe((routeParams) => {
        const field = this.fieldList().find((p) => {
          return p.id === routeParams.fieldId;
        });

        this.onFieldSelected(field);
      });
    } catch (error) {
      this.logger.error('field-list ngOnInit error', error);
    }

    const [unusedFields, { items: projects }] = await Promise.all([
      this.getUnusedFields(),
      this.projectService.getProjects(),
    ]);
    this.unusedFields.set(unusedFields);
    this.projects.set(projects.sort((a, b) => a.name.localeCompare(b.name)));
  }

  private async getUnusedFields() {
    return await this.fieldService.getUnusedFields(this.groupService.getSelectedGroupId());
  }

  private populateStoredSortValue(sortId: number) {
    this.currentSort.set(Math.abs(sortId));
    this.reverseSort.set(Math.sign(sortId) < 0);
  }

  async onDeleteUnusedFields() {
    this.isUnusedFieldsLoading.set(true);
    const unusedFields = await this.getUnusedFields();
    this.unusedFields.set(unusedFields);
    this.isUnusedFieldsLoading.set(false);

    const translations = this.translocoService.translateObject('field-list.confirm-delete-fields', {
      content: {
        count: this.unusedFields().length,
        total: this.fieldList().length,
      },
      'delete-success': {
        count: this.unusedFields().length,
      },
    });

    if (!this.unusedFields().length) {
      this.dialogService.showSimpleDialog({
        title: translations['no-unused-fields-title'],
        content: translations['no-unused-fields-content'],
      });
      return;
    }

    this.dialogService
      .showWarningPopup({
        title: translations['title'],
        content: translations['content'],
        confirmTitle: translations['delete'],
        cancelTitle: translations['cancel'],
        confirmColor: SimpleDialogButtonColor.red,
      })
      .afterClosed()
      .subscribe(async (result) => {
        if (result) {
          try {
            this.isUnusedFieldDeleting = true;

            await this.fieldService.deleteUnusedFields(this.groupService.getSelectedGroupId());
            this.unusedFields.set([]);
            this.alertService.showToast(translations['delete-success']);
          } catch (error) {
            this.alertService.showErrorToast(translations['delete-failed'], error);
          } finally {
            this.isUnusedFieldDeleting = false;
          }
        }
      });
  }

  onFieldClicked(event: MouseEvent, field: Field): void {
    event.stopPropagation();
    event.preventDefault();
    this.onFieldSelected(field, false, event);
  }

  onFieldSelected(field: Field, autoScroll = false, event: any = undefined) {
    const url = `groups/${this.groupId}/fields/${field ? field.id : ''}`;
    if (event?.metaKey || event?.detail?.metaKey) {
      window.open(url, '_blank');
      return;
    }
    this.router.navigate([url]).then((navigated) => {
      if ((event && !navigated) || (this.selectedField() && this.selectedField() === field)) {
        return;
      }

      this.selectedField.set(field);
    });
    if (autoScroll) {
      setTimeout(() => {
        const listItem = document.getElementById(this.selectedField().id);
        listItem?.scrollIntoView({ behavior: 'smooth' });
      }, 1000);
    }
  }

  ngOnDestroy() {
    this.unsub$.next();
    this.unsub$.complete();
  }

  async refresh(): Promise<void> {
    this.isLoading.set(true);
    this.allFields = await this.fieldService.getFields(false);
    if (!this.allFields.count) {
      this.translocoService
        .selectTranslate('field-list.no-metadata-unused')
        .pipe(takeUntil(this.unsub$))
        .subscribe((translation) => {
          this.deleteUnusedFieldsTooltip.set(translation);
        });
    }

    this.setCurrentFields();
    this.isLoading.set(false);
  }
  private setCurrentFields(selectedFieldId = null): void {
    this.allFields.items = this.allFields.items.filter((f) => f.metadata?.display !== false);
    this.fieldService.sortFields(this.allFields.items, this.currentSort(), this.reverseSort());
    this.fieldList.set(this.allFields.items);
    this.filteredFields.set(this.allFields.items);
    if (this.isUnusedFieldDeleting && this.showDeleteUnusedFieldsButton()) {
      this.filteredFields.set([]);
      this.selectedField.set(null);
    }
    this.filterFieldsBySearch();
    const params = this.route.firstChild?.snapshot.paramMap;
    let fieldId = selectedFieldId ?? params?.get('fieldId');
    if (this.isUnusedFieldDeleting) {
      fieldId = this.displayedFields()[0]?.id;
    }

    if (fieldId && fieldId !== this.selectedField()?.id) {
      const field = this.fieldList().find((p) => {
        return p.id === fieldId;
      });
      this.onFieldSelected(field);
    }
  }

  getFieldIcon(iconUrl: string): string {
    if (iconUrl) {
      return this.iconService.getByTypeAndId('field-icons', iconUrl);
    }
    return this.DEFAULT_FIELD_ICON;
  }

  setSortType(sortId: number) {
    this.populateStoredSortValue(sortId);

    this.preferencesService
      .savePreference('fieldSorting', this.currentSort() * (this.reverseSort() ? -1 : 1))
      .catch((reason) => {
        this.alertService.showErrorToast(
          this.translocoService.translate('field-list.failed-to-save'),
          reason
        );
      });
    this.fieldService.sortFields(this.displayedFields(), this.currentSort(), this.reverseSort());
  }

  private filterFieldsBySearch() {
    const searchTextLc = this.searchText.toLocaleLowerCase();
    this.displayedFields.set(
      this.filteredFields().filter(
        (s) =>
          s.label.toLocaleLowerCase().indexOf(searchTextLc) >= 0 ||
          s.description.toLocaleLowerCase().indexOf(searchTextLc) >= 0 ||
          s.id.toLocaleLowerCase().indexOf(searchTextLc) >= 0 ||
          s.normalizedName.toLocaleLowerCase().indexOf(searchTextLc) >= 0 ||
          s.fieldName.toLocaleLowerCase().indexOf(searchTextLc) >= 0 ||
          s.sideloaded?.xiFieldLabels?.some((fl) => {
            const localeToUse = Object.keys(fl.label.locales).find((key) =>
              key.includes(this.translocoService.getActiveLang())
            );
            if (localeToUse) {
              return fl.label.locales[localeToUse].toLocaleLowerCase().indexOf(searchTextLc) >= 0;
            }
            return fl.default.toLocaleLowerCase().indexOf(searchTextLc) >= 0;
          })
      )
    );
  }

  internalProps = {
    searchBox: {
      placeholder: '',
      onChangeOverride: ($event: SearchBox_onChangeOverrideEvent) => {
        this.ngZone.run(() => {
          this.searchText = $event.searchText;
          this.filterFieldsBySearch();
        });
      },
    },
  };

  onShowNewMetadataFieldModal() {
    this.showNewMetadataFieldModal.set(true);
  }

  async onNewMetadataFieldModalClosed(result: FieldResult) {
    this.showNewMetadataFieldModal.set(false);
    const res = await this.fieldService.createMetadataField(result);
    if (res) {
      this.onFieldSelected(res, true);
    }
  }

  onShowFilterFieldsPopout() {
    this.isFilterFieldsPopoutVisible.set(true);
  }

  onFilterFieldsPopoutClosed(data: {
    filteredFields: Field[];
    areFiltersApplied: boolean;
    onlyUnusedFieldsApplied: boolean;
  }) {
    this.isFilterFieldsPopoutVisible.set(false);
    if (!data) {
      return;
    }
    this.areFiltersApplied.set(data.areFiltersApplied);
    if (data.filteredFields) {
      this.filteredFields.set(data.filteredFields);
    } else {
      this.filteredFields.set(this.allFields.items);
    }
    this.showDeleteUnusedFieldsButton.set(data.onlyUnusedFieldsApplied);
    this.filterFieldsBySearch();
    this.fieldService.sortFields(this.displayedFields(), this.currentSort(), this.reverseSort());
  }
}
